import React, { useCallback, useEffect, useRef, useState } from "react";
import { withTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { fetchActiveTelemetry } from "../FieldApp/fieldAppApi";
import {
  assumeRelativeParts,
  buildFieldAppTrackingHref,
  markTelemetrySessionsSeen,
  nextTelemetryPopup,
} from "./dashboardActiveTelemetry";
import "./DashboardActiveTelemetryKpi.css";

const POLL_MS = 30000;

function relativeLabel(parts, t) {
  if (!parts || parts.unit === "now") return t("agora");
  if (parts.unit === "minutes") return t("há {{count}} min", { count: parts.count });
  if (parts.unit === "hours") return t("há {{count}} h", { count: parts.count });
  return t("há {{count}} d", { count: parts.count });
}

function DashboardActiveTelemetryKpi({ farm_id, t }) {
  const history = useHistory();
  const seenKeysRef = useRef(null);
  const popupOpenRef = useRef(false);
  const [sessions, setSessions] = useState([]);
  const [popupSession, setPopupSession] = useState(null);
  const [now, setNow] = useState(() => new Date());
  const [loading, setLoading] = useState(true);

  popupOpenRef.current = Boolean(popupSession);

  const loadSessions = useCallback(async () => {
    if (!farm_id) return;
    try {
      const data = await fetchActiveTelemetry(farm_id);
      const nextSessions = (data && data.sessions) || [];
      const result = nextTelemetryPopup(seenKeysRef.current, nextSessions, {
        popupOpen: popupOpenRef.current,
      });
      seenKeysRef.current = result.keys;
      setSessions(nextSessions);
      if (!popupOpenRef.current && result.popup) {
        setPopupSession(result.popup);
      }
      setNow(new Date());
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [farm_id]);

  useEffect(() => {
    loadSessions();
    const timer = window.setInterval(loadSessions, POLL_MS);
    return () => window.clearInterval(timer);
  }, [loadSessions]);

  const dismissPopup = () => {
    seenKeysRef.current = markTelemetrySessionsSeen(
      seenKeysRef.current,
      sessions
    );
    setPopupSession(null);
  };

  const openTracking = (session) => {
    const href = buildFieldAppTrackingHref(farm_id, session);
    if (href) history.push(href);
  };

  const panelClass = [
    "x_panel",
    "no-margin-top",
    "dashboard-active-telemetry",
    popupSession ? "dashboard-active-telemetry--alert" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="col-xs-12 col-md-3 col-lg-3 col-sm-6">
      <div className={panelClass}>
        <div className="dashboard-active-telemetry__header">
          {t("Telemetria ativa")}
          {sessions.length ? (
            <span className="dashboard-active-telemetry__count">
              {sessions.length}
            </span>
          ) : null}
        </div>

        {popupSession ? (
          <div className="dashboard-active-telemetry__popup">
            <p className="dashboard-active-telemetry__popup-text">
              {t("{{name}} assumiu a telemetria {{relative}}", {
                name: popupSession.operatorName,
                relative: relativeLabel(
                  assumeRelativeParts(popupSession.assumedAt, now),
                  t
                ),
              })}
            </p>
            {popupSession.machineIdent ? (
              <p className="dashboard-active-telemetry__machine">
                {popupSession.machineIdent}
              </p>
            ) : null}
            <button
              type="button"
              className="btn btn-xs btn-default dashboard-active-telemetry__close"
              onClick={dismissPopup}
            >
              {t("Fechar")}
            </button>
          </div>
        ) : loading ? (
          <p className="dashboard-active-telemetry__empty text-muted">
            {t("Carregando…")}
          </p>
        ) : sessions.length === 0 ? (
          <p className="dashboard-active-telemetry__empty text-muted">
            {t("Ninguém com telemetria ativa")}
          </p>
        ) : (
          <ul className="dashboard-active-telemetry__list">
            {sessions.map((session) => (
              <li
                key={
                  session.sessionUuid ||
                  `${session.operatorId}-${session.assumedAt}`
                }
                className="dashboard-active-telemetry__item"
              >
                <button
                  type="button"
                  className="dashboard-active-telemetry__name"
                  onClick={() => openTracking(session)}
                  title={t("Abrir no rastreamento")}
                >
                  {session.operatorName}
                </button>
                <span className="dashboard-active-telemetry__meta">
                  {session.machineIdent
                    ? `${session.machineIdent} · `
                    : ""}
                  {relativeLabel(
                    assumeRelativeParts(session.assumedAt, now),
                    t
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default withTranslation()(DashboardActiveTelemetryKpi);
